import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import { FaSearch, FaFilter, FaGraduationCap, FaArrowUp, FaClock, FaCalendarAlt, FaStar, FaUserGraduate, FaMoneyBillWave, FaChevronLeft, FaChevronRight, FaShareAlt, FaLayerGroup } from 'react-icons/fa';
import { ArrowLeft } from 'lucide-react';
import Swal from 'sweetalert2';
import Loader from '../shared/Loader';
import { API_URL } from '../../config';
import DualRangeSlider from '../shared/DualRangeSlider';
import ShareModal from '../shared/ShareModal';
import './BrowsePage.css';

const BrowseCourses = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const { search: urlSearch } = useLocation();

    const [courses, setCourses] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [loading, setLoading] = useState(true);

    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Process URL params
    const queryParams = new URLSearchParams(urlSearch);
    const [search, setSearch] = useState(queryParams.get('q') || '');
    const [filterLevel, setFilterLevel] = useState(queryParams.get('level') || '');
    const [filterCategory, setFilterCategory] = useState(queryParams.get('category') || '');
    const [filterPrice, setFilterPrice] = useState('all'); // all, free, paid

    const [categories, setCategories] = useState([]);
    const [showScrollTop, setShowScrollTop] = useState(false);

    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const [minPrice, setMinPrice] = useState(0);
    const [maxPrice, setMaxPrice] = useState(1000000);
    const [selectedIndustries, setSelectedIndustries] = useState([]);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [shareData, setShareData] = useState({ url: '', title: '' });

    // Scroll listener for Scroll-to-Top button
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 400) setShowScrollTop(true);
            else setShowScrollTop(false);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    useEffect(() => {
        axios.get('/api/public/courses?limit=100')
            .then(r => {
                setCourses(r.data);
                setFiltered(r.data);
                // Extract available categories immediately
                const cats = [...new Set(r.data.map(c => c.category).filter(Boolean))];
                setCategories(cats.sort((a, b) => a.localeCompare(b)));
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);


    // Update categories dynamically — SHOW ONLY CATEGORIES WITH AVAILABLE COURSES
    useEffect(() => {
        if (courses.length > 0) {
            const availableCategories = [...new Set(courses.map(c => c.category).filter(Boolean))];
            setCategories(availableCategories.sort((a, b) => a.localeCompare(b)));
        }
    }, [courses]);

    useEffect(() => {
        let result = courses;

        if (search) {
            result = result.filter(c =>
                c.title.toLowerCase().includes(search.toLowerCase()) ||
                (c.tutorId?.name || '').toLowerCase().includes(search.toLowerCase())
            );
        }

        if (filterLevel) {
            result = result.filter(c => (c.difficultyLevel || '').toLowerCase().replace(/[^a-z]/g, '') === filterLevel.toLowerCase().replace(/[^a-z]/g, ''));
        }
        if (filterCategory) {
            result = result.filter(c => (c.category || '').toLowerCase() === filterCategory.toLowerCase());
        }
        if (selectedIndustries.length > 0) {
            result = result.filter(c => selectedIndustries.includes(c.category));
        }

        if (filterPrice !== 'all') {
            if (filterPrice === 'free') result = result.filter(c => (c.pricingType || '').toLowerCase() === 'free');
            if (filterPrice === 'paid') result = result.filter(c => (c.pricingType || '').toLowerCase() === 'paid');
        }

        // Helper to extract numeric value
        const parseNum = (val) => {
            if (typeof val === 'number') return val;
            if (!val) return 0;
            const match = val.toString().replace(/,/g, '').match(/\d+/);
            return match ? parseInt(match[0]) : 0;
        };

        if (minPrice) result = result.filter(c => parseNum(c.price) >= parseNum(minPrice));
        if (maxPrice) result = result.filter(c => parseNum(c.price) <= parseNum(maxPrice));

        setFiltered(result);
        setCurrentPage(1);
    }, [search, filterLevel, filterCategory, selectedIndustries, filterPrice, minPrice, maxPrice, courses]);

    // Paginated Data
    const paginatedItems = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const totalPages = Math.ceil(filtered.length / pageSize);

    // Handle Suggestions
    useEffect(() => {
        if (!search || search.length < 2) {
            setSuggestions([]);
            return;
        }
        const lower = search.toLowerCase();
        const matches = courses
            .filter(c => c.title.toLowerCase().includes(lower))
            .slice(0, 5)
            .map(c => ({ type: 'course', text: c.title, id: c._id }));
        setSuggestions(matches);
    }, [search, courses]);

    const handleReset = () => {
        setSearch('');
        setFilterLevel('');
        setFilterCategory('');
        setFilterPrice('all');
        setMinPrice(0);
        setMaxPrice(1000000);
        setSelectedIndustries([]);
        navigate('/browse/courses', { replace: true });
    };

    const handleCourseClick = (courseId) => {
        navigate(`/public-course/${courseId}`);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleShare = (e, course) => {
        e.stopPropagation();
        const shareUrl = `${window.location.origin}/public-course/${course._id}`;
        setShareData({ url: shareUrl, title: course.title });
        setIsShareModalOpen(true);
    };

    if (loading) return <Loader fullScreen={true} />;

    return (
        <div className="browse-root">
            {/* HERO V2 */}
            <div className="browse-hero-v2">
                <Link to="/" className="browse-back-btn">
                    <ArrowLeft size={18} />
                    <span>Back</span>
                </Link>
                <div className="browse-hero-skyline"></div>
                <h1>Find Professional Courses</h1>

                <div className="browse-pill-search-wrap glass-wrap">
                    <div className="browse-pill-search">
                        <div className="search-icon-box">
                            <FaSearch />
                        </div>
                        <input
                            type="text"
                            placeholder="Course Title or keyword"
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setShowSuggestions(true);
                            }}
                            onFocus={() => setShowSuggestions(true)}
                        />
                        <button className="search-prime-btn">Search</button>
                    </div>

                    {/* Suggestions Dropdown */}
                    {showSuggestions && suggestions.length > 0 && (
                        <div className="pp-search-suggestions" style={{ top: '100%', left: '20px', right: '20px' }}>
                            {suggestions.map((s, idx) => (
                                <div
                                    key={idx}
                                    className="pp-suggestion-item"
                                    onClick={() => {
                                        setSearch(s.text);
                                        setShowSuggestions(false);
                                    }}
                                >
                                    <FaSearch className="pp-suggest-icon" />
                                    <span>{s.text}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className={`sidebar-overlay ${isSidebarOpen ? 'visible' : ''}`} onClick={() => setIsSidebarOpen(false)}></div>

            <div className="browse-layout-container">
                {/* SIDEBAR */}
                <aside className={`browse-sidebar ${isSidebarOpen ? 'open' : ''}`}>
                    <div className="filter-card">
                        <div className="filter-header">
                            <FaFilter /> Filter
                        </div>

                        <div className="filter-group">
                            <label>Difficulty Level</label>
                            <select
                                className="filter-input"
                                value={filterLevel}
                                onChange={(e) => setFilterLevel(e.target.value)}
                            >
                                <option value="">All Levels</option>
                                <option value="Beginner">Beginner</option>
                                <option value="Intermediate">Intermediate</option>
                                <option value="Advanced">Advanced</option>
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Course Category</label>
                            <select
                                className="filter-input"
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                            >
                                <option value="">All Categories</option>
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>{cat}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label>Price Range</label>
                            <div className="filter-range-wrap">
                                <DualRangeSlider 
                                    min={0}
                                    max={1000000}
                                    step={1000}
                                    minValue={minPrice}
                                    maxValue={maxPrice}
                                    onChange={({ min, max }) => {
                                        setMinPrice(min);
                                        setMaxPrice(max);
                                    }}
                                />
                                <div className="range-inputs">
                                    <div className="range-box">
                                        <span>Min : ({minPrice})</span>
                                        <input type="number" placeholder="0" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} />
                                    </div>
                                    <div className="range-box">
                                        <span>Max : ({maxPrice})</span>
                                        <input type="number" placeholder="1000000" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="filter-group">
                            <label>Pricing</label>
                            <div className="modern-selection-grid">
                                {[
                                    { label: 'All', value: 'all' },
                                    { label: 'Free', value: 'free' },
                                    { label: 'Paid', value: 'paid' }
                                ].map((item) => (
                                    <div 
                                        key={item.value} 
                                        className={`modern-selection-item ${filterPrice === item.value ? 'active' : ''}`}
                                        onClick={() => setFilterPrice(item.value)}
                                    >
                                        <span>{item.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button className="browse-reset-btn" onClick={handleReset}>
                            Reset All Filters
                        </button>
                    </div>
                </aside>

                {/* MAIN CONTENT */}
                <main className="browse-main-content">
                    <div className="results-info-bar">
                        <div className="results-header-left">
                            <div className="results-count">
                                {filtered.length} Course{filtered.length !== 1 ? 's' : ''} Found
                            </div>
                        </div>
                        <div className="results-header-right">
                            <div className="entries-select-wrap">
                                <span>Show:</span>
                                <select
                                    className="entries-select"
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                </select>
                            </div>
                            <button className="mobile-filter-trigger" onClick={() => setIsSidebarOpen(true)}>
                                <FaFilter />
                            </button>
                        </div>
                    </div>

                    <div className="browse-results-list">
                        {paginatedItems.length === 0 ? (
                            <div className="pp-empty-premium">
                                <div className="pp-ep-icon-wrap"><FaGraduationCap /></div>
                                <h3>No Courses Match Your Search</h3>
                                <p>Try adjusting your filters or search term.</p>
                                <div className="pp-ep-glow"></div>
                            </div>
                        ) : (
                            filtered.map(course => (
                                <div
                                    key={course._id}
                                    className="browse-result-card"
                                    onClick={() => handleCourseClick(course._id)}
                                >
                                    <div className="card-logo-box" style={{ padding: "0px" }} >
                                        {course.thumbnailUrl ? (
                                            <img
                                                src={course.thumbnailUrl.startsWith('http') ? course.thumbnailUrl : `${API_URL}${course.thumbnailUrl.startsWith('/') ? '' : '/'}${course.thumbnailUrl}`}
                                                alt="Thumbnail"
                                            />
                                        ) : (
                                            <FaGraduationCap className="card-logo-ph" />
                                        )}
                                    </div>

                                    <div className="card-main-info">
                                        <div className="card-top-row">
                                            <h3 className="card-title">{course.title}</h3>
                                            <div className="card-share-btn" onClick={(e) => handleShare(e, course)}>
                                                <FaShareAlt />
                                            </div>
                                        </div>
                                        <div className="card-company">
                                            {course.tutorId?.name || 'Expert Instructor'}
                                        </div>

                                        <div className="card-meta-list">
                                            {course.category && <div className="card-meta-item"><FaLayerGroup /> {course.category}</div>}
                                            {course.difficultyLevel && <div className="card-meta-item"><FaUserGraduate /> {course.difficultyLevel}</div>}
                                            <div className="card-meta-item"><FaMoneyBillWave /> {course.pricingType === 'Free' ? 'Free' : (course.price ? `$${course.price}` : 'Paid')}</div>
                                        </div>

                                        <div className="card-tags">
                                            {course.category && <span className="card-tag">{course.category}</span>}
                                            {course.difficultyLevel && <span className="card-tag">{course.difficultyLevel}</span>}
                                        </div>

                                        <div className="card-footer">
                                            {course.updatedAt && (
                                                <div className="card-deadline">
                                                    <FaCalendarAlt /> Updated {new Date(course.updatedAt).toLocaleDateString()}
                                                </div>
                                            )}
                                            <button
                                                className="card-apply-btn"
                                                onClick={(e) => { e.stopPropagation(); handleCourseClick(course._id); }}
                                            >
                                                Start Learning
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="browse-pagination">
                            <button
                                className="pg-btn"
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            >
                                <FaChevronLeft />
                            </button>

                            {[...Array(totalPages)].map((_, i) => (
                                <div
                                    key={i}
                                    className={`pg-num ${currentPage === i + 1 ? 'active' : ''}`}
                                    onClick={() => handlePageChange(i + 1)}
                                >
                                    {i + 1}
                                </div>
                            ))}

                            <button
                                className="pg-btn"
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            >
                                <FaChevronRight />
                            </button>
                        </div>
                    )}
                </main>
            </div>

            {/* ===== SCROLL TO TOP ===== */}
            <button
                className={`pp-scroll-top ${showScrollTop ? 'visible' : ''}`}
                onClick={scrollToTop}
                aria-label="Scroll to top"
            >
                <FaArrowUp />
            </button>

            <ShareModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                shareUrl={shareData.url}
                title={shareData.title}
            />
        </div>
    );
};

export default BrowseCourses;
